import { parseDateLocal } from "./format";

export type Health = "green" | "amber" | "red";

type HealthProject = {
  status: string;
  targetDate?: string | null;
  completedCount: number;
  totalCount: number;
  healthOverride?: Health | null;
  healthReason?: string | null;
};

export const HEALTH_LABEL: Record<Health, string> = {
  green: "On track",
  amber: "At risk",
  red: "Off track",
};
export const HEALTH_COLOR: Record<Health, string> = {
  green: "#16a34a",
  amber: "#d97706",
  red: "#dc2626",
};

/**
 * Project health with an explanation. A manual override wins; otherwise it's
 * derived from schedule (target date) and progress (% complete).
 */
export function projectHealth(p: HealthProject): {
  health: Health;
  reason: string;
  overridden: boolean;
} {
  if (p.healthOverride) {
    return {
      health: p.healthOverride,
      reason: p.healthReason || "Manually set by the owner.",
      overridden: true,
    };
  }
  const pct =
    p.totalCount > 0 ? Math.round((p.completedCount / p.totalCount) * 100) : 0;
  const done = p.status === "archived" || (p.totalCount > 0 && pct === 100);
  if (done)
    return { health: "green", reason: "All work complete.", overridden: false };

  const target = parseDateLocal(p.targetDate ?? null);
  const now = new Date();
  const msLeft = target ? target.getTime() - now.getTime() : null;
  const daysLeft = msLeft != null ? msLeft / 86_400_000 : null;

  if (daysLeft != null && daysLeft < 0)
    return {
      health: "red",
      reason: `Past target date with ${pct}% complete.`,
      overridden: false,
    };
  if (daysLeft != null && daysLeft <= 14 && pct < 60)
    return {
      health: "amber",
      reason: `Target in ${Math.ceil(daysLeft)}d but only ${pct}% complete.`,
      overridden: false,
    };
  return {
    health: "green",
    reason: target ? `${pct}% complete, on schedule.` : `${pct}% complete.`,
    overridden: false,
  };
}
